/**
 * Supabase Notifications API Service
 *
 * Handles all CRUD operations for notifications
 */

import { supabase } from './client';
import type { Notification, NotificationInsert, NotificationUpdate, Json } from './database.types';

// ============================================
// Constants
// ============================================

export const notificationTypeLabels: Record<Notification['type'], string> = {
  info: 'Info',
  success: 'Success',
  warning: 'Warning',
  error: 'Error',
  lead: 'New Lead',
  facebook_lead: 'Facebook Lead',
  scheduled_quote: 'Scheduled Quote',
};

export const notificationTypeColors: Record<Notification['type'], string> = {
  info: '#3B82F6', // blue
  success: '#10B981', // green
  warning: '#F59E0B', // amber
  error: '#EF4444', // red
  lead: '#8B5CF6', // purple
  facebook_lead: '#1877F2', // facebook blue
  scheduled_quote: '#8B5CF6', // purple (matches in-person quote color)
};

// ============================================
// Frontend Data Type
// ============================================

export interface NotificationData {
  id: string;
  createdAt: string;
  updatedAt: string;
  title: string;
  message: string;
  type: Notification['type'];
  read: boolean;
  readAt?: string;
  link?: string;
  metadata: Record<string, unknown>;
}

// ============================================
// Type Mappings
// ============================================

const mapToFrontend = (row: Notification): NotificationData => ({
  id: row.id,
  createdAt: row.created_at,
  updatedAt: row.updated_at,
  title: row.title,
  message: row.message,
  type: row.type,
  read: row.read,
  readAt: row.read_at || undefined,
  link: row.link || undefined,
  metadata: (row.metadata as Record<string, unknown>) || {},
});

// ============================================
// API Functions
// ============================================

export interface GetNotificationsParams {
  read?: boolean;
  type?: Notification['type'] | Notification['type'][];
  page?: number;
  pageSize?: number;
  sortOrder?: 'asc' | 'desc';
}

export interface GetNotificationsResponse {
  notifications: NotificationData[];
  total: number;
  page: number;
  pageSize: number;
}

export const notificationsApi = {
  /**
   * Create a new notification
   */
  async create(data: {
    title: string;
    message: string;
    type?: Notification['type'];
    link?: string;
    metadata?: Record<string, unknown>;
  }): Promise<NotificationData | null> {
    const insertData: NotificationInsert = {
      title: data.title,
      message: data.message,
      type: data.type || 'info',
      link: data.link || null,
      metadata: (data.metadata || {}) as Json,
      read: false,
    };

    const { data: result, error } = await supabase
      .from('notifications')
      .insert(insertData)
      .select()
      .single();

    if (error) {
      // console.error('Error creating notification:', error);
      return null;
    }

    return mapToFrontend(result as Notification);
  },

  /**
   * Create a Facebook lead notification
   */
  async createFacebookLeadNotification(leadName: string, leadId: string): Promise<NotificationData | null> {
    return this.create({
      title: 'New Facebook Lead',
      message: `${leadName} just submitted a lead from Facebook!`,
      type: 'facebook_lead',
      link: `/dashboard/customers/${leadId}`,
      metadata: { leadId, leadName, source: 'facebook' },
    });
  },

  /**
   * Create a scheduled in-person quote notification
   */
  async createScheduledQuoteNotification(
    customerName: string,
    scheduledDate: Date,
    address?: string
  ): Promise<NotificationData | null> {
    const dateStr = scheduledDate.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
    const timeStr = scheduledDate.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    });

    return this.create({
      title: 'New In-Person Quote Scheduled',
      message: `${customerName} - ${dateStr} at ${timeStr}${address ? ` (${address})` : ''}`,
      type: 'scheduled_quote',
      link: '/dashboard/calendar',
      metadata: {
        customerName,
        scheduledDate: scheduledDate.toISOString(),
        address: address || null,
      },
    });
  },

  /**
   * Get all notifications with filtering and pagination
   */
  async getAll(params: GetNotificationsParams = {}): Promise<GetNotificationsResponse> {
    const {
      read,
      type,
      page = 1,
      pageSize = 20,
      sortOrder = 'desc',
    } = params;

    let query = supabase.from('notifications').select('*', { count: 'exact' });

    // Filter by read status
    if (read !== undefined) {
      query = query.eq('read', read);
    }

    // Filter by type
    if (type) {
      if (Array.isArray(type)) {
        query = query.in('type', type);
      } else {
        query = query.eq('type', type);
      }
    }

    // Sorting
    query = query.order('created_at', { ascending: sortOrder === 'asc' });

    // Pagination
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;
    query = query.range(from, to);

    const { data, error, count } = await query;

    if (error) {
      // console.error('Error fetching notifications:', error);
      return { notifications: [], total: 0, page, pageSize };
    }

    return {
      notifications: (data as Notification[]).map(mapToFrontend),
      total: count || 0,
      page,
      pageSize,
    };
  },

  /**
   * Get unread notification count
   */
  async getUnreadCount(): Promise<number> {
    const { count, error } = await supabase
      .from('notifications')
      .select('*', { count: 'exact', head: true })
      .eq('read', false);

    if (error) {
      // console.error('Error getting unread count:', error);
      return 0;
    }

    return count || 0;
  },

  /**
   * Mark a notification as read
   */
  async markAsRead(id: string): Promise<NotificationData | null> {
    const { data, error } = await supabase
      .from('notifications')
      .update({ read: true, read_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();

    if (error) {
      // console.error('Error marking notification as read:', error);
      return null;
    }

    return mapToFrontend(data as Notification);
  },

  /**
   * Mark all notifications as read
   */
  async markAllAsRead(): Promise<boolean> {
    const { error } = await supabase
      .from('notifications')
      .update({ read: true, read_at: new Date().toISOString() })
      .eq('read', false);

    if (error) {
      // console.error('Error marking all notifications as read:', error);
      return false;
    }

    return true;
  },

  /**
   * Delete a notification
   */
  async delete(id: string): Promise<boolean> {
    const { error } = await supabase
      .from('notifications')
      .delete()
      .eq('id', id);

    if (error) {
      // console.error('Error deleting notification:', error);
      return false;
    }

    return true;
  },

  /**
   * Delete all read notifications
   */
  async deleteAllRead(): Promise<boolean> {
    const { error } = await supabase
      .from('notifications')
      .delete()
      .eq('read', true);

    if (error) {
      // console.error('Error deleting read notifications:', error);
      return false;
    }

    return true;
  },
};

export default notificationsApi;
